import {Injectable} from '@angular/core';
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {Snapshot} from "../models/snpashot.model";
import {Lead} from "../models/lead.model";
import {ReferralsDgSearchResults} from "../models/datagrid-search-results/referrals.dg-search-results";
import {Partner} from "../models/partner.model";

/**
 * Serwis warstwy danych obsługujący program poleceń oraz zarządzanie Ambasadorami.
 * Wykorzystuje istniejący model Leadów do śledzenia podmiotów z polecenia
 * i pozwala na przekształcanie użytkowników w autoryzowanych Partnerów/Ambasadorów.
 */
@Injectable({
  providedIn: 'root'
})
export class ReferralsService implements DataGridServiceInterface<Observable<ReferralsDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę poleconych podmiotów (Referrals) dla widoku DataGrid.
   *
   * @param limit Maksymalna liczba rekordów.
   * @param offset Przesunięcie względem początku.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Kolumna sortująca.
   * @param showDeletedObjects Flaga widoczności elementów usuniętych.
   * @returns Observable emitujący wyniki wyszukiwania (mapowane na obiekty typu Lead).
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<ReferralsDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/referrals/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new ReferralsDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new Lead(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje określone podmioty z polecenia na podstawie przekazanych filtrów.
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa kryteriów wyszukiwania.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Kolumna sortująca.
   * @param showDeletedObjects Flaga widoczności elementów usuniętych.
   * @returns Observable emitujący przefiltrowaną listę poleceń.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<ReferralsDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/referrals/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new ReferralsDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new Lead(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Aktualizuje profil poleconego Leada.
   *
   * @param leadEdited Zmodyfikowany obiekt Leada.
   * @returns Observable emitujący zaktualizowany obiekt.
   */
  updateLead(leadEdited: Lead): Observable<Lead> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/referrals', leadEdited).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Modyfikuje wewnętrzną notatkę/komentarz do poleconego podmiotu.
   *
   * @param leadUUID Unikalny identyfikator poleconego Leada.
   * @param comment Treść notatki.
   * @returns Observable emitujący zaktualizowany obiekt Leada.
   */
  updateLeadComment(leadUUID: string, comment: string): Observable<Lead> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/referrals/comment', {
      'uuid': leadUUID,
      'comment': comment
    }).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera dziennik audytu (historię zmian) dla profilu poleconego podmiotu.
   *
   * @param uuid Unikalny identyfikator podmiotu.
   * @returns Observable emitujący tablicę migawek zdarzeń (Snapshot).
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/leads/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełne szczegóły profilu poleconego Leada.
   *
   * @param uuid Unikalny identyfikator poleconego Leada.
   * @returns Observable emitujący obiekt szczegółów.
   */
  getLeadDetails(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/referrals/details/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Rejestruje nowego Ambasadora w systemie.
   *
   * @param partnerObj Obiekt z danymi nowego Ambasadora.
   * @returns Observable emitujący utworzony profil.
   */
  addPartner(partnerObj: Partner) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/ambassador', partnerObj).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Partner>(new Partner(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera treść regulaminu programu ambasadorskiego, wymaganego do akceptacji
   * przy dołączaniu do programu poleceń.
   *
   * @returns Observable emitujący treść regulaminu (string).
   */
  getAmbassadorTerms() {
    return this.apiService.get<VetineoApiResponse>('/dashboard/ambassador-terms').pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.value;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Aktualizuje profil istniejącego Ambasadora.
   *
   * @param ambassadorObj Zmodyfikowany obiekt Partnera/Ambasadora.
   * @returns Observable emitujący zaktualizowany profil.
   */
  updateAmbassador(ambassadorObj: Partner) : Observable<Partner> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/ambassador', ambassadorObj).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Partner>(new Partner(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
